/**
 * Returns the voices for a given language code.
 */
// https://stackoverflow.com/a/61963317/3049315
function getLocaleVoices(locale: string): SpeechSynthesisVoice[] {
	if (!window.speechSynthesis) {
		throw new Error('Browser does not support speech synthesis')
	}
	if (!globalThis.voices) {
		throw new Error('Voices not loaded yet. Call loadVoicesWhenAvailable() first.')
	}
	if (globalThis.voiceLocaleMap[locale]) {
		return globalThis.voiceLocaleMap[locale]
	}

	globalThis.voiceLocaleMap[locale] = globalThis.voices.filter((voice: SpeechSynthesisVoice) => voice.lang === locale)
	return globalThis.voiceLocaleMap[locale]
}

// https://stackoverflow.com/a/61963317/3049315
export function speakText(text: string): void {
	
	const utterance = new window.SpeechSynthesisUtterance();
	const locale = "en-US"; // Default to English US
	utterance.lang = locale;
	utterance.rate = 0.9;
	utterance.pitch = 1.0;
	utterance.text = text;
	utterance.volume = 1.0;

	globalThis.isActuallyPaused = false;

	// Wait for voices to load (especially in Chrome)
	const setVoice = () => {
		const voices = getLocaleVoices(locale);
		//console.log("Available voices:", voices);

		// Try to find a male voice by name or language
		const maleVoice = voices.find(v =>
			/Male|John|David|Alex|Matthew|Andrew|Guy|Brian|Christopher|Eric|Roger|Steffan/i.test(v.name)
		);

		if (maleVoice) {
			utterance.voice = maleVoice;
		} else {
			utterance.voice = voices[0]; // Fallback to the first voice
		}
		//console.log("Selected voice:", utterance.voice.name, "for locale:", locale);

		// Cancel any existing speech
		window.speechSynthesis.cancel();
		window.speechSynthesis.speak(utterance);
	};

	setVoice();
}

export function pauseSpeech() {
	// On Microsoft Edge, the `window.speechSynthesis.paused` property is not reliable.
	// It returns false even when speech is paused.
    if (window.speechSynthesis.speaking && !globalThis.isActuallyPaused) {
      window.speechSynthesis.pause();
	  globalThis.isActuallyPaused = true;
    }
}

export function resumeSpeech() {
	if (globalThis.isActuallyPaused) {
		window.speechSynthesis.resume();
		globalThis.isActuallyPaused = false;
	}
}

/**
 * Formats a date using Intl.DateTimeFormat.
 *
 * @param date A date string (e.g. "2024-03-09", "Present") or undefined.
 * @returns A formatted date, the original string, or an empty string.
 */
export function formatDate(date?: string): string {
	if (!date) {
		return "";
	}

	const parsedDate = new Date(date);

	if (Number.isNaN(parsedDate.getTime())) {
		return date;
	}

	return new Intl.DateTimeFormat("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric",
		timeZone: "UTC",
	}).format(parsedDate);
}

/**
 * Gets the year from a date string.
 *
 * @param date A date string (e.g. "2024-03-09", "Present") or undefined.
 * @returns The year, the original string, or an empty string.
 */
export function getYear(date?: string): string {
	if (!date) {
		return "";
	}

	const parsedDate = new Date(date);

	return Number.isNaN(parsedDate.getTime())
		? date
		: parsedDate.getUTCFullYear().toString();
}

/**
 * Sanitizes a string to prevent Cross-Site Scripting (XSS) attacks.
 * Escapes special characters to their HTML entities.
 * 
 * @param str The string to sanitize.
 * @returns The sanitized string.
 */
export function sanitizeHTML(text: string): string {
	const div = document.createElement('div');
	div.textContent = text;
	return div.innerHTML;
}

/**
 * Truncate HTML content safely, preserving valid HTML structure.
 * 
 * @param html - The HTML content to truncate.
 * @param maxLength - The character limit for truncation.
 * @returns A safely truncated HTML string.
 */
export function truncateHtml(html: string, maxLength: number): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, "text/html");
	const div = doc.body;

	let currentLength = 0;
	let isTruncated = false;

	const truncateNode = (node: ChildNode): boolean => {
		if (node.nodeType === Node.TEXT_NODE) {
			const textNode = node as Text;
			const remaining = maxLength - currentLength;

			if (remaining <= 0) {
				isTruncated = true;
				textNode.nodeValue = "";
				return false;
			}

			if (textNode.nodeValue!.length > remaining) {
				textNode.nodeValue = textNode.nodeValue!.substring(0, remaining) + " ...";
				currentLength = maxLength;
				isTruncated = true;
				return false;
			}

			currentLength += textNode.nodeValue!.length;
		} else if (node.nodeType === Node.ELEMENT_NODE) {
			const elementNode = node as Element;
			const childNodes = Array.from(elementNode.childNodes);

			for (const child of childNodes) {
				if (!truncateNode(child)) {
					// Remove leftover sibling nodes after truncation
					while (child.nextSibling) {
						elementNode.removeChild(child.nextSibling);
					}
					return false;
				}
			}
		}

		return currentLength < maxLength;
	};

	const truncateRoot = (node: ParentNode): void => {
		const childNodes = Array.from(node.childNodes);

		for (const child of childNodes) {
			if (!truncateNode(child)) {
				// Remove any leftover nodes after truncation
				while (child.nextSibling) {
					node.removeChild(child.nextSibling);
				}
				break;
			}
		}
	};

	truncateRoot(div);

	// Return truncated HTML, ensuring tags are properly closed
	return div.innerHTML;
}

/**
 * Get the language's hexadecimal color,
 * 
 * @param language The language to get the color for
 * @returns The hexadecimal color of the language parameter
 */
export function getLanguageColor(language: string): string {
	let color: (string | null) = null;
	language = language.toLowerCase();

	if (languageColors[language]) {
		color = languageColors[language];
	}
	else if (language.startsWith("c++") ||
			language.startsWith("catch2") ||
			language.startsWith("googletest") ||
			language.startsWith("cppcheck") ||
			language.startsWith("cpplint") ||
			language.startsWith("doxygen") ||
			language.startsWith("qt creator") ||
			language.startsWith("pybind11") ||
			language.startsWith("spdlog") ||
			language.startsWith("argparse")) {
		color = languageColors.cpp;
	} else if (language === ("c") ||
			language.startsWith("cmake") ||
			language.startsWith("gdb") ||
			language.startsWith("valgrind") ||
			language.startsWith("iwyu") ||
			language.startsWith("clion") ||
			language.startsWith("conan") ||
			language.startsWith("clang-")) {
		color = languageColors.c;
	} else if (language.startsWith("python") ||
			language.startsWith("pytest") ||
			language.startsWith("unittest") ||
			language.startsWith("tkinter") ||
			language.startsWith("pyinstaller") ||
			language.startsWith("pystray") ||
			language.startsWith("requests") ||
			language.startsWith("configparser") ||
			language.startsWith("discord.py")) {
		color = languageColors.python;
	} else if (language === ("javadoc")) {
		color = languageColors.java;
	} else if (language.startsWith("javascript") ||
			language.startsWith("ecmascript") ||
			language.startsWith("jquery") ||
			language.startsWith("react") ||
			language.startsWith("node") ||
			language.startsWith("ace editor") ||
			language.startsWith("webpack") ||
			language === "jest" ||
			language.endsWith(".js")) {
		color = languageColors.javascript;
	} else if (language.startsWith("html") ||
			language.startsWith("open graph protocol") ||
			language.startsWith("aria")) {
		color = languageColors.html;
	} else if (language.startsWith("css") ||
			language.startsWith("bootstrap") ||
			language.startsWith("font awesome") ||
			language.startsWith("google fonts")) {
		color = languageColors.css;
	} else if (language.startsWith("sass")) {
		color = languageColors.scss;
	} else if (language.startsWith("swf")) {
		color = languageColors.actionscript;
	} else if (language.includes("sql") ||
			language.startsWith("database")) {
		color = languageColors.plsql;
	} else if (language.startsWith("junit") ||
			language.startsWith("gradle")) {
		color = languageColors.java;
	} else if (language.startsWith("c#") ||
			language.includes(".net") ||
			language.startsWith("winforms")) {
		color = languageColors.csharp;
	} else if (language.includes("assembly")) {
		color = languageColors.assembly;
	} else if (language.startsWith("php")) {
		color = languageColors.php;
	} else if (language.startsWith("arduino")) {
		color = languageColors.arduino;
	} else if (language.startsWith("github actions") ||
			language.startsWith("appveyor")) {
		color = languageColors.yaml;
	} else if (language.startsWith("git")) {
		color = languageColors.git;
	}
	return color;
}

/**
 * Get the language's CSS color styles.
 * 
 * @param language The language to get the color for
 * @returns The CSS color styles for this language.
 */
export function setLanguageStyle(language: string): string {

	let color = getLanguageColor(language);
	let style = "";
	if (color) {
		style += "background-color: " + color + ";";
		if (isBright(color)) {
			style += "color: black;";
		}
	}

	return style;
}

/**
 * Check if a color is bright.
 * 
 * @param color The color to check, either as an hexadecimal, rgb or rgba CSS string.
 * @returns True if the color is bright, false otherwise.
 */
// Taken from: https://awik.io/determine-color-bright-dark-using-javascript/
function isBright(color: string): boolean {
	// Variables for red, green, blue values
	let r:number, g:number, b:number, hsp:number;
	// Check the format of the color, HEX or RGB?
	if (color.match(/^rgb/)) {
		// If RGB --> store the red, green, blue values in separate variables
		let rgb = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);
		r = parseFloat(rgb[1]);
		g = parseFloat(rgb[2]);
		b = parseFloat(rgb[3]);
	} else {
		// If hex --> Convert it to RGB: http://gist.github.com/983661
		let rgb = +("0x" + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));
		r = rgb >> 16;
		g = rgb >> 8 & 255;
		b = rgb & 255;
	}
	// HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
	hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
	// Using the HSP value, determine whether the color is light or dark
	if (hsp > 127.5) {
		return true;
	} else {
		return false;
	}
}

/**
 * This function (pSBC) will take a HEX or RGB web color.
 * pSBC can shade it darker or lighter, or blend it with a second color, and can also pass
 * it right through but convert from Hex to RGB (Hex2RGB) or RGB to Hex (RGB2Hex).
 * All without you even knowing what color format you are using.
 * 
 * @param p Percentage (-1 to 1) to blend or shade. Negative to darken. Positive to lighten.
 * @param c0 The color to blend from or shade.
 * @param c1 The color to blend to (if exists).
 * @param l Pass true to use Linear Blending.
 * @returns True if the color is bright, false otherwise.
 */
// Taken from: https://stackoverflow.com/a/13542669/3049315
// https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)#stackoverflow-archive-begin
// Version 4.0
export const pSBC = (p: number, c0: string, c1?:string, l?: boolean): string => {
	let r:any,g:any,b:any,P:any,f:any,t:any,h:any,i=parseInt,m=Math.round,a:any=typeof(c1)=="string";
	if(typeof(p)!="number"||p<-1||p>1||typeof(c0)!="string"||(c0[0]!='r'&&c0[0]!='#')||(c1&&!a))return null;
	// MangaD - removed 'this' as it was causing problems with TypeScript
	let pSBCr=(d:any)=>{
		let n:any=d.length,x:any={};
		if(n>9){
			[r,g,b,a]=d=d.split(","),n=d.length;
			if(n<3||n>4)return null;
			x.r=i(r[3]=="a"?r.slice(5):r.slice(4)),x.g=i(g),x.b=i(b),x.a=a?parseFloat(a):-1
		}else{
			if(n==8||n==6||n<4)return null;
			if(n<6)d="#"+d[1]+d[1]+d[2]+d[2]+d[3]+d[3]+(n>4?d[4]+d[4]:"");
			d=i(d.slice(1),16);
			if(n==9||n==5)x.r=d>>24&255,x.g=d>>16&255,x.b=d>>8&255,x.a=m((d&255)/0.255)/1000;
			else x.r=d>>16,x.g=d>>8&255,x.b=d&255,x.a=-1
		}return x};
	h=c0.length>9,h=a?c1.length>9?true:c1=="c"?!h:false:h,f=pSBCr(c0),P=p<0,t=c1&&c1!="c"?pSBCr(c1):P?{r:0,g:0,b:0,a:-1}:{r:255,g:255,b:255,a:-1},p=P?p*-1:p,P=1-p;
	if(!f||!t)return null;
	if(l)r=m(P*f.r+p*t.r),g=m(P*f.g+p*t.g),b=m(P*f.b+p*t.b);
	else r=m((P*f.r**2+p*t.r**2)**0.5),g=m((P*f.g**2+p*t.g**2)**0.5),b=m((P*f.b**2+p*t.b**2)**0.5);
	a=f.a,t=t.a,f=a>=0||t>=0,a=f?a<0?t:t<0?a:a*P+t*p:0;
	if(h)return"rgb"+(f?"a(":"(")+r+","+g+","+b+(f?","+m(a*1000)/1000:"")+")";
	else return"#"+(4294967296+r*16777216+g*65536+b*256+(f?m(a*255):0)).toString(16).slice(1,f?undefined:-2)
}


/**
 * Unescape certain HTML entities in the given string.
 * 
 * @param s The string to unescape the HTML entities
 * @returns The given string with unescaped HTML entities
 */
export function unescapeHTMLEntities(text: string): string {
	const textarea = document.createElement('textarea');
	textarea.innerHTML = text;
	return textarea.value;
}


/**
 * Get the month name for a given number from 0 to 11
 * 
 * @param number The month number, from 0 to 11
 * @returns The month name
 */
export function toMonthString(monthIndex: number): string {
	const months = [
		"January", "February", "March", "April", "May", "June",
		"July", "August", "September", "October", "November", "December"
	];
	return months[monthIndex] || "";
}

/**
 * Sets a cookie for the website.
 * 
 * @param cname The cookie key/name.
 * @param cvalue The cookie value.
 * @param exdays The expiration date of the cookie in days.
 */
function setCookie(cname: string, cvalue: string, exdays: number) {
	var d = new Date();
	d.setTime(d.getTime() + (exdays*24*60*60*1000));
	var expires = "expires="+ d.toUTCString();
	document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/**
 * Gets a cookie value.
 * 
 * @param cname The cookie key/name.
 * @returns The cookie value.
 */
function getCookie(cname: string): string {
	var name = cname + "=";
	var ca = document.cookie.split(';');
	for(var i = 0; i < ca.length; i++) {
		var c = ca[i];
		while (c.charAt(0) == ' ') {
			c = c.substring(1);
		}
		if (c.indexOf(name) == 0) {
			return c.substring(name.length, c.length);
		}
	}
	return "";
}

/**
 * Checks if a browser has support for CSS variables.
 * 
 * @returns A boolean indicating whether the browser supports CSS variables or not.
 */
function browserCanUseCssVariables(): boolean {
	return window.CSS && window.CSS.supports && window.CSS.supports('--fake-var', "0");
}

interface ILangColor {
	[key: string]: string
}


// Colors taken from:
// https://gist.github.com/robertpeteuil/bb2dc86f3b3e25d203664d61410bfa30

// More colors can be found here:
// https://github.com/ozh/github-colors/blob/master/colors.json
const languageColors: ILangColor = {
	abap : "#e8274b",
	actionscript : "#882b0f",
	ada : "#02f88c",
	agda : "#315665",
	agsscript : "#b9d9ff",
	alloy : "#64c800",
	ampl : "#e6efbb",
	antlr : "#9dc3ff",
	apiblueprint : "#2acca8",
	apl : "#5a8164",
	arc : "#aa2afe",
	asciidoc : "#73a0c5",
	arduino : "#bd79d1",
	asp : "#6a40fd",
	aspectj : "#a957b0",
	assembly : "#6e4c13",
	ats : "#1ac620",
	autohotkey : "#6594b9",
	autoit : "#1c3552",
	blitzmax : "#cd6400",
	boo : "#d4bec1",
	brainfuck : "#2f2530",
	csharp : "#178600",
	c : "#555555",
	chapel : "#8dc63f",
	cirru : "#ccccff",
	clarion : "#db901e",
	clean : "#3f85af",
	click : "#e4e6f3",
	clojure : "#db5855",
	coffeescript : "#244776",
	coldfusioncfc : "#ed2cd6",
	coldfusion : "#ed2cd6",
	commonlisp : "#3fb68b",
	componentpascal : "#b0ce4e",
	cpp : "#f34b7d",
	crystal : "#776791",
	css : "#563d7c",
	d : "#ba595e",
	dart : "#00b4ab",
	diff : "#88dddd",
	dm : "#447265",
	dogescript : "#cca760",
	dylan : "#6c616e",
	e : "#ccce35",
	eagle : "#814c05",
	ec : "#913960",
	ecl : "#8a1267",
	edn : "#db5855",
	eiffel : "#946d57",
	elixir : "#6e4a7e",
	elm : "#60b5cc",
	emacslisp : "#c065db",
	emberscript : "#fff4f3",
	erlang : "#b83998",
	"f#" : "#b845fc",
	factor : "#636746",
	fancy : "#7b9db4",
	fantom : "#dbded5",
	flux : "#88ccff",
	forth : "#341708",
	fortran : "#4d41b1",
	freemarker : "#0050b2",
	frege : "#00cafe",
	gamemakerlanguage : "#8fb200",
	git : "#F44D27",
	glyph : "#e4cc98",
	gnuplot : "#f0a9f0",
	go : "#375eab",
	golo : "#88562a",
	gosu : "#82937f",
	grammaticalframework : "#79aa7a",
	groovy : "#e69f56",
	handlebars : "#01a9d6",
	harbour : "#0e60e3",
	haskell : "#29b544",
	haxe : "#df7900",
	html : "#e44b23",
	hy : "#7790b2",
	idl : "#a3522f",
	io : "#a9188d",
	ioke : "#078193",
	isabelle : "#fefe00",
	j : "#9eedff",
	java : "#b07219",
	javascript : "#f1e05a",
	jflex : "#dbca00",
	jsoniq : "#40d47e",
	julia : "#a270ba",
	jupyternotebook : "#da5b0b",
	kotlin : "#f18e33",
	krl : "#28431f",
	lasso : "#999999",
	latte : "#a8ff97",
	lex : "#dbca00",
	lfe : "#004200",
	livescript : "#499886",
	lolcode : "#cc9900",
	lookml : "#652b81",
	lsl : "#3d9970",
	lua : "#000080",
	makefile : "#427819",
	mask : "#f97732",
	matlab : "#bb92ac",
	max : "#c4a79c",
	maxscript : "#00a6a6",
	mercury : "#ff2b2b",
	metal : "#8f14e9",
	mirah : "#c7a938",
	mtml : "#b7e1f4",
	ncl : "#28431f",
	nemerle : "#3d3c6e",
	nesc : "#94b0c7",
	"netlinx" : "#0aa0ff",
	"netlinx+erb" : "#747faa",
	netlogo : "#ff6375",
	newlisp : "#87aed7",
	nimrod : "#37775b",
	nit : "#009917",
	nix : "#7e7eff",
	nu : "#c9df40",
	"objective-c" : "#438eff",
	"objective-c++" : "#6866fb",
	"objective-j" : "#ff0c5a",
	ocaml : "#3be133",
	omgrofl : "#cabbff",
	ooc : "#b0b77e",
	opal : "#f7ede0",
	oxygene : "#cdd0e3",
	oz : "#fab738",
	pan : "#cc0000",
	papyrus : "#6600cc",
	parrot : "#f3ca0a",
	pascal : "#b0ce4e",
	pawn : "#dbb284",
	perl : "#0298c3",
	perl6 : "#0000fb",
	php : "#4f5d95",
	piglatin : "#fcd7de",
	pike : "#005390",
	plantuml : "#fbbd16",
	plsql : "#dad8d8",
	pogoscript : "#d80074",
	processing : "#0096d8",
	prolog : "#74283c",
	propellerspin : "#7fa2a7",
	puppet : "#302b6d",
	puredata : "#91de79",
	purebasic : "#5a6986",
	purescript : "#1d222d",
	python : "#3572a5",
	qml : "#44a51c",
	r : "#198ce7",
	racket : "#22228f",
	ragelinrubyhost : "#9d5200",
	raml : "#77d9fb",
	rebol : "#358a5b",
	red : "#ee0000",
	"ren'py" : "#ff7f7f",
	rouge : "#cc0088",
	ruby : "#701516",
	rust : "#dea584",
	saltstack : "#646464",
	sas : "#b34936",
	scss : "#c6538c",
	scala : "#dc322f",
	scheme : "#1e4aec",
	self : "#0579aa",
	shell : "#89e051",
	shen : "#120f14",
	slash : "#007eff",
	slim : "#ff8f77",
	smalltalk : "#596706",
	sourcepawn : "#5c7611",
	sqf : "#3f3f3f",
	squirrel : "#800000",
	stan : "#b2011d",
	standardml : "#dc566d",
	supercollider : "#46390b",
	swift : "#ffac45",
	systemverilog : "#dae1c2",
	tcl : "#e4cc98",
	tex : "#3d6117",
	turing : "#45f715",
	typescript : "#2b7489",
	unifiedparallelc : "#4e3617",
	unity3dasset : "#ab69a1",
	unrealscript : "#a54c4d",
	vala : "#fbe5cd",
	verilog : "#b2b7f8",
	vhdl : "#adb2cb",
	viml : "#199f4b",
	visualbasic : "#945db7",
	volt : "#1f1f1f",
	vue : "#2c3e50",
	webontologylanguage : "#9cc9dd",
	wisp : "#7582d1",
	x10 : "#4b6bef",
	xbase : "#403a40",
	xc : "#99da07",
	xquery : "#5232e7",
	yacc : "#4B6C4B",
	yaml : "#cb171e",
	zephir : "#118f9e",
};